import type { ScheduleDay, ScheduleObject } from '@/graphql/types';
import { addDays, getDay, setDay, startOfWeek } from 'date-fns';
import { find, findKey, get, isEqual, last, range, upperFirst } from 'lodash-es';
import { useObservableState } from 'observable-hooks';
import { useCallback, useMemo } from 'react';
import { getWeekDayWide, useLocale } from '../../../../utils/date-fns';
import { useOverrideState, useSharedFormContext } from '../../utils';
import type { PROJECT_OPTION } from '../ChoiceField';

export type PROJECT_OPTION_TYPE = typeof PROJECT_OPTION;

export enum Measure {
    ALL_DAY = 'ALL_DAY',
    NEVER = 'NEVER',
    CUSTOM = 'CUSTOM',
}

export type DaySchedule = {
    [Property in keyof Omit<ScheduleDay, '__typename'>]-?: string;
};

export type XAble = keyof DaySchedule;

export type WeekDay = keyof Required<Omit<ScheduleObject, '__typename'>>;

export type Schedule = {
    [Property in keyof Omit<ScheduleObject, '__typename'>]-?: DaySchedule;
};

export const translations: Record<Measure, string> = {
    [Measure.ALL_DAY]: gettext('MEASURE_ALL_DAY'),
    [Measure.NEVER]: gettext('MEASURE_NEVER'),
    [Measure.CUSTOM]: gettext('MEASURE_CUSTOM'),
};

const startOfTheDay = '00:00';
const endOfTheDay = '23:59';

export const presets: Record<Measure, DaySchedule> = {
    [Measure.ALL_DAY]: {
        enable: startOfTheDay,
        disable: endOfTheDay,
    },
    [Measure.NEVER]: {
        enable: startOfTheDay,
        disable: startOfTheDay,
    },
    [Measure.CUSTOM]: {
        enable: '08:00',
        disable: '18:00',
    },
};

export function getPresetForDaySchedule(daySchedule: DaySchedule): Measure {
    return (
        (findKey(presets, (preset) => isEqual(preset, daySchedule)) as Measure) || Measure.CUSTOM
    );
}

// keep the order of the week days consistent with the english locale.
const weekDayKeys: WeekDay[] = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
];

/**
 * Custom hook to manage the week days and their properties.
 *
 * It exposes the following:
 * - `week`: An array of objects representing the week days, each with properties:
 * - `getNextWeekDay`: A function to get the next week day based on the current week day.
 * - `lastDOW`: The last day of the week. e.g.: 'sunday'.
 */
function useWeek() {
    const locale = useLocale();

    const week = useMemo(() => {
        const firstDOW = startOfWeek(new Date(), { locale });
        return range(7).map((i) => {
            const date = addDays(firstDOW, i);
            const dayNumber = getDay(date);
            const weekDayKey = weekDayKeys[dayNumber];
            return {
                // E.g.: monday.
                weekDay: weekDayKey,
                // E.g.: Monday - this is a translating value.
                displayName: upperFirst(getWeekDayWide(locale, dayNumber)),
                // E.g.: 1.
                number: dayNumber,
            };
        });
    }, [locale]);

    const getWeekDayByNumber = useCallback(
        (number: number): WeekDay => find(week, ['number', number])!.weekDay,
        [week]
    );

    const getNextWeekDayNumber = useCallback(
        (weekDay: string): number =>
            getDay(addDays(setDay(new Date(), find(week, ['weekDay', weekDay])!.number), 1)),
        [week]
    );

    const getNextWeekDay = useCallback(
        (weekDay: string): WeekDay => getWeekDayByNumber(getNextWeekDayNumber(weekDay)),
        [getNextWeekDayNumber, getWeekDayByNumber]
    );

    const lastDOW = useMemo(() => last(week)!.weekDay, [week]);

    return {
        getNextWeekDay,
        week,
        lastDOW,
    };
}

/**
 * Custom hook to manage the schedule field.
 *
 * It handles synchronization of the schedule state with the form context,
 * allows updating the schedule for a specific week day,
 * and provides a function to copy the schedule from one day to the next.
 */
export function useScheduleManager(
    name: string,
    schedule: Schedule,
    onChange: (schedule: Schedule) => void
) {
    const { getNextWeekDay, week, lastDOW } = useWeek();
    const { formController } = useSharedFormContext();
    const parentValues = useObservableState(formController.parentValues$, null);
    const { overridable } = useOverrideState(name);

    const updateWeekday = useCallback(
        (weekDay: WeekDay, daySchedule: Partial<DaySchedule>) => {
            const updated = {
                ...schedule,
                [weekDay]: { ...schedule[weekDay], ...daySchedule },
            } as Schedule;

            // Don't update if nothing changed.
            if (!isEqual(schedule, updated)) {
                onChange(updated);
            }
        },
        [schedule, onChange]
    );

    // Copy the schedule from the current day to the next day.
    // It will also mark the next day as overridden.
    const copyToNextDay = useCallback(
        (weekDay: WeekDay) => {
            const nextWeekDay = getNextWeekDay(weekDay);
            const currentSchedule = schedule[weekDay] || {};
            const nextDaySchedule = schedule[nextWeekDay] || {};
            const nextDayParentValue = get(
                parentValues,
                `${name}.${nextWeekDay}`,
                {}
            ) as DaySchedule;

            if (!isEqual(nextDaySchedule, currentSchedule)) {
                updateWeekday(nextWeekDay, currentSchedule);

                if (overridable) {
                    // If the next day parent schedule is not the same as the current day schedule,
                    // we mark the field as overridden
                    const overridden = !isEqual(nextDayParentValue, currentSchedule);

                    formController.setOverriddenField(
                        `${name}.${nextWeekDay}`,
                        overridden,
                        overridden
                    );
                }
            }
        },
        [getNextWeekDay, schedule, parentValues, name, updateWeekday, overridable, formController]
    );

    return {
        copyToNextDay,
        updateWeekday,
        week,
        lastDOW,
        getNextWeekDay,
    };
}
